import { Download } from '@mui/icons-material'
import { Box, Button } from '@mui/material'
import { CarrierVisitStatus } from '@operations/app/api'
import { messageBus, useOperationsStore } from '@operations/AppProvider'
import { VesselVisitKPIContainer } from '@operations/features/vesselVisitKPIs/components/organisms/VesselVisitKPIContainer'
import { VesselVisitKPIHeader } from '@operations/features/vesselVisitKPIs/components/organisms/VesselVisitKPIHeader'
import { VesselVisitKPIStats } from '@operations/features/vesselVisitKPIs/models/vessel-kpi'
import { VesselVisitKPIUIStore } from '@operations/features/vesselVisitKPIs/stores/vessel-visit-kpi.ui-store'
import { delay } from '@operations/utils/delay'
import { downloadHtmlAsPDF } from '@operations/utils/export-html'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router'

type Props = {
  vesselVisitKPIStats: VesselVisitKPIStats
  visitOverviewComponent: (minutesLeft: number) => React.ReactNode
}

export const VesselVisitKPI = observer(({ visitOverviewComponent, vesselVisitKPIStats }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { carrierVisitStore, appStore } = useOperationsStore()
  const { id } = useParams<{ id: string }>()

  const visitId = Number(id)

  const store = useMemo(() => new VesselVisitKPIUIStore(messageBus, appStore), [appStore])

  useAsyncFetch(async () => {
    store.setVesselVisitId(visitId)
    await carrierVisitStore.loadOne(visitId)
    await store.getExecutedMoves(visitId)
  }, [store, carrierVisitStore, visitId])

  const status = carrierVisitStore.items.find(item => item.id === visitId)?.status
  const isVisitCompleted =
    status === CarrierVisitStatus.Departed || status === CarrierVisitStatus.Closed

  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false)

  const divId = 'tpr-container'

  const downloadHtmlAsFile = async () => {
    setIsDownloadingPDF(true)
    appStore.increaseLoadingCounter()

    requestAnimationFrame(async () => {
      await downloadHtmlAsPDF(divId, 'terminal-performance-report', 'portrait')
      await delay(1000)
      appStore.decreaseLoadingCounter()
      setIsDownloadingPDF(false)
    })
  }

  return (
    <Box
      sx={{
        bgcolor: theme.palette.grey[100],
        minHeight: '100%',
      }}
    >
      <VesselVisitKPIHeader
        carrierVisitStore={carrierVisitStore}
        visitId={visitId}
        rightRenderOption={
          isVisitCompleted && (
            <Button
              variant='contained'
              color='primary'
              data-cy='upload-orders-btn'
              onClick={downloadHtmlAsFile}
              startIcon={<Download />}
            >
              {t('download', 'Download')}
            </Button>
          )
        }
      />
      <VesselVisitKPIContainer
        visitId={visitId}
        vesselVisitKPIUIStore={store}
        isVisitCompleted={isVisitCompleted}
        vesselVisitKPIStats={vesselVisitKPIStats}
        visitOverviewComponent={visitOverviewComponent}
        divId={divId}
        isDownloadingPDF={isDownloadingPDF}
      />
    </Box>
  )
})
